import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { authApi } from "@/api/auth";
import { ApiError, setUnauthorizedHandler } from "@/api/client";
import type { MeDto } from "@/types/api";
import { AuthContext, type AuthContextValue } from "@/hooks/authContext";
import { effectivePrimaryRole } from "@/lib/auth/rolePrecedence";
import { TimeZoneProvider } from "@/design-system/lib/timeZone";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<MeDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
      } else {
        setError(err instanceof Error ? err.message : "Unable to resolve session.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Redirect to sign-in the moment any request 401s on a tab that believed
  // it had a session (e.g. the session expired server-side, or the user
  // signed out in another tab), instead of leaving a stale cached page on
  // screen until the next full reload — mirrors the portal's
  // setPortalUnauthorizedHandler. A 401 with no known session (the initial
  // /auth/me probe while signed out) is left to RequireAuth's own redirect.
  const userRef = useRef<MeDto | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!userRef.current) return;
      userRef.current = null;
      setUser(null);
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.assign("/login");
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    try {
      const me = await authApi.login(email, password);
      setUser(me);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.errors?.[0] ?? err.message);
      } else {
        setError("Sign-in failed.");
      }
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const perms = new Set(user?.permissions ?? []);
    const roleSet = new Set(user?.roleCodes ?? []);
    return {
      user,
      loading,
      error,
      primaryRoleCode: effectivePrimaryRole(user),
      hasPermission: (perm: string) => perms.has(perm),
      hasAnyPermission: (list: string[]) => list.some((p) => perms.has(p)),
      hasRoleCode: (code: string) => roleSet.has(code),
      login,
      logout,
      refresh,
    };
  }, [user, loading, error, login, logout, refresh]);

  // The staff tree is the one that HAS a viewer zone, so it is the one that
  // supplies it. Design-system components (`<Timestamp>` and anything else
  // that renders an instant) read it from the ambient context instead of each
  // of ~74 call sites remembering to thread `tz` down by hand.
  //
  // The portal tree deliberately mounts no equivalent: `PortalUser` has no
  // timezone column and `PortalMeDto` no `TimeZone` field, so there is nothing
  // to supply. Its timestamps stay on `FALLBACK_TIMEZONE` — unchanged, and now
  // visibly unsupplied rather than falsely resolved. See
  // `@/design-system/lib/timeZone`.
  return (
    <AuthContext.Provider value={value}>
      <TimeZoneProvider tz={user?.timeZone ?? null}>{children}</TimeZoneProvider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>.");
  }
  return ctx;
}

/**
 * Variant of {@link useAuth} that returns <c>null</c> when no
 * <c>AuthProvider</c> wraps the tree, instead of throwing. Used by
 * components that want to render a permission-gated affordance inside
 * tests that don't bootstrap auth (the affordance simply doesn't render).
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext) ?? null;
}
